import Phaser from 'phaser';
import {
  ARENA,
  OBSTACLES,
  NET_CONFIG,
  DATA,
  TICK_MS,
  itemDef,
  type AbilitySlot,
  type ClassId,
  type CombatFx,
  type EnemyType,
} from '@gladiators/shared';
import {
  buildTextures,
  SHEET_KEY,
  TILES,
  CLASS_TINT,
  GOBLIN_TINT,
  ENEMY_TINT,
  BOSS_TINT,
  enemySheet,
  bossSheet,
} from '../textures.js';
import type { RuntimeContext } from '../game/context.js';
import type { EnemyView } from '../net/state.js';
import type { Body } from '../game/localPlayer.js';

const SPRITE_SCALE = 2;
const MAX_STEPS_PER_FRAME = 5;

/** How long a combat FX kind stays on screen, in ms. */
function fxLifetimeMs(kind: CombatFx['kind']): number {
  switch (kind) {
    case 'hit':
      return 180;
    case 'abilityNova':
    case 'abilityBlink':
    case 'abilityBulwark':
    case 'abilityFrostNova':
    case 'abilityBarrier':
    case 'abilityCircleHeal':
    case 'abilityBlessing':
    case 'abilityRoots':
      return 320;
    case 'abilityBolt':
    case 'abilityHeal':
    case 'abilityCharge':
    case 'abilityWhirlwind':
    case 'abilityFireball':
    case 'abilityPierce':
    case 'abilitySmite':
    case 'abilityMoonfire':
    case 'abilityRegrowth':
    case 'abilityShadowBolt':
    case 'abilityDrainLife':
      return 240;
    case 'abilityCorruption':
      return 320;
    case 'abilityMeteor':
    case 'abilitySanctuary':
    case 'abilityEntanglingStorm':
      return 420;
    case 'abilityRapidFire':
      return 360;
    case 'abilitySoulHarvest':
      return 380;
    case 'abilityCleave':
      return 200;
    case 'enemyExplode':
      return 360;
    case 'enemyShoot':
      return 160;
    case 'enemyHeal':
      return 320;
    default:
      return 120;
  }
}

interface Rig {
  sprite: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  bar: Phaser.GameObjects.Graphics;
  sheet: string;
}

export class ArenaScene extends Phaser.Scene {
  private ctx!: RuntimeContext;
  private players = new Map<string, Rig>();
  private enemies = new Map<number, Rig>();
  private fxLayer!: Phaser.GameObjects.Graphics;
  /** feature 3.4 — enemy item drops on the floor, drawn under the entities. */
  private groundItemLayer!: Phaser.GameObjects.Graphics;
  /** feature 5.3a — floor-hazard zones, drawn beneath everything else. */
  private hazardLayer!: Phaser.GameObjects.Graphics;
  /** feature 5.2 — the boss's bespoke pillar obstacles (world-space, under entities). */
  private bossPillarLayer!: Phaser.GameObjects.Graphics;
  /** feature 5.2 — the screen-fixed boss health bar + cast bar + phase pips.
   * feature 5.3b — one bar per boss entity (up to 2, stacked). */
  private bossBarLayer!: Phaser.GameObjects.Graphics;
  private bossBarTexts: Phaser.GameObjects.Text[] = [];
  /** feature 1.5 — ground danger-zone telegraphs, drawn under the entities. */
  private telegraphLayer!: Phaser.GameObjects.Graphics;
  /** feature 1.5 — skillshot projectile marks, drawn over the entities. */
  private projectileLayer!: Phaser.GameObjects.Graphics;
  private fxQueue: { fx: CombatFx; until: number }[] = [];
  private simAccum = 0;
  private lastAuthStamp = 0;
  /** Last known pointer position in world coords, for keyboard ability casts. */
  private lastPointer = { x: 0, y: 0 };

  constructor() {
    super('arena');
  }

  init(): void {
    this.ctx = this.game.registry.get('ctx') as RuntimeContext;
  }

  preload(): void {
    this.load.spritesheet(SHEET_KEY, 'assets/kenney_tiny_dungeon.png', {
      frameWidth: 16,
      frameHeight: 16,
    });
  }

  create(): void {
    buildTextures(this);

    this.cameras.main.setBounds(0, 0, ARENA.width, ARENA.height);
    this.cameras.main.setBackgroundColor('#1b1f16');
    this.cameras.main.setRoundPixels(true);

    this.add
      .tileSprite(0, 0, ARENA.width, ARENA.height, SHEET_KEY, TILES.groundBase)
      .setOrigin(0, 0)
      .setDepth(-10);

    for (const o of OBSTACLES) {
      const frame = o.id.startsWith('wall') ? TILES.wall : TILES.rock;
      this.add
        .tileSprite(o.x, o.y, o.w, o.h, SHEET_KEY, frame)
        .setOrigin(0, 0)
        .setDepth(o.y + o.h);
      const outline = this.add.graphics().setDepth(o.y + o.h);
      outline.lineStyle(2, 0x000000, 0.25).strokeRect(o.x, o.y, o.w, o.h);
    }

    this.hazardLayer = this.add.graphics().setDepth(2);
    this.groundItemLayer = this.add.graphics().setDepth(4);
    this.telegraphLayer = this.add.graphics().setDepth(5);
    this.bossPillarLayer = this.add.graphics().setDepth(3);
    this.fxLayer = this.add.graphics().setDepth(9000);
    this.projectileLayer = this.add.graphics().setDepth(9001);
    this.bossBarLayer = this.add.graphics().setScrollFactor(0).setDepth(9500);
    this.bossBarTexts = [0, 1].map(() =>
      this.add
        .text(0, 0, '', {
          fontFamily: 'monospace',
          fontSize: '13px',
          color: '#ffe8e0',
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setScrollFactor(0)
        .setDepth(9501),
    );

    this.setupInput();

    this.ctx.onFx = (list: CombatFx[]) => {
      const now = performance.now();
      for (const fx of list) this.fxQueue.push({ fx, until: now + fxLifetimeMs(fx.kind) });
    };
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.ctx.onFx = null;
    });

    const local = this.ctx.room.state.players.get(this.ctx.sessionId);
    if (local) this.ctx.local.reset(local.x, local.y);
  }

  private setupInput(): void {
    const kb = this.input.keyboard;
    if (!kb) return;

    // Movement is click-to-move only (A* path around obstacles). There is no
    // WASD / arrow-key or hold-mouse movement — Q/W/E/R are ability keys.
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      const world = this.cameras.main.getWorldPoint(p.x, p.y);
      this.lastPointer.x = world.x;
      this.lastPointer.y = world.y;
    });

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      const world = this.cameras.main.getWorldPoint(p.x, p.y);
      this.lastPointer.x = world.x;
      this.lastPointer.y = world.y;
      if (p.rightButtonDown() || p.button === 2 || !this.localControllable()) return;
      this.ctx.local.issueClick(world.x, world.y);
    });
    this.input.mouse?.disableContextMenu();

    // Ability hotkeys: Q / W / E / R cast on key-down at the world cursor.
    // Effects resolve server-side; nothing here is predicted (1.1).
    const abilityKeys: [string, AbilitySlot][] = [
      ['Q', 'q'],
      ['W', 'w'],
      ['E', 'e'],
      ['R', 'r'],
    ];
    for (const [code, slot] of abilityKeys) {
      kb.addKey(code, false).on('down', () => this.castAbility(slot));
    }
  }

  private castAbility(slot: AbilitySlot): void {
    if (!this.localControllable()) return;
    this.ctx.sendCast(slot, this.lastPointer.x, this.lastPointer.y);
  }

  private localControllable(): boolean {
    if (this.ctx.room.state.phase !== 'match') return false;
    const me = this.ctx.room.state.players.get(this.ctx.sessionId);
    return !!me && !me.dead;
  }

  override update(_t: number, deltaMs: number): void {
    const dt = Math.min(deltaMs, 100) / 1000;
    const controllable = this.localControllable();

    // feature 5.1b — thread the server's crowd control into the local predictor
    // so it stops (stun) / slows (slow) instead of drifting-then-snapping. Read
    // before the reconcile/replay + prediction steps below so the gate applies to
    // both. This is PREDICTION, not feedback: it must match the server's
    // `World.tickMatch` gate, which freezes / slows on ANY `stun` / `slow`
    // regardless of source — so Whirlwind's OWN self-slow is included here (the
    // enemy-sourced-only `p.stunned` / `p.slowed` schema flags drive the tint +
    // HUD row instead).
    const meNow = this.ctx.room.state.players.get(this.ctx.sessionId);
    const statuses = Array.from(meNow?.statuses ?? []);
    this.ctx.local.stunnedByServer =
      !meNow?.dead && statuses.some((s) => s.kind === 'stun' && s.remainingMs > 0);
    const slowSt = statuses.find((s) => s.kind === 'slow' && s.remainingMs > 0);
    this.ctx.local.slowMag = slowSt?.magnitude ?? 0;

    // Reconcile against the newest authoritative pose (may be lag-delayed).
    const auth = this.ctx.localAuth;
    if (auth && auth.stamp !== this.lastAuthStamp) {
      this.lastAuthStamp = auth.stamp;
      if (controllable) this.ctx.local.reconcile(auth.x, auth.y, auth.lastSeq);
      else this.ctx.local.reset(auth.x, auth.y);
    }

    // Fixed-step prediction: one input per server tick.
    this.simAccum += deltaMs;
    let steps = 0;
    while (this.simAccum >= TICK_MS && steps < MAX_STEPS_PER_FRAME) {
      this.simAccum -= TICK_MS;
      steps++;
      if (controllable) {
        this.ctx.local.bodies = this.collectBodies();
        this.ctx.sendInput(this.ctx.local.advance());
      }
    }
    if (steps === MAX_STEPS_PER_FRAME) this.simAccum = 0;

    this.ctx.local.smooth(dt);

    this.syncPlayers();
    this.syncEnemies();
    this.drawHazards();
    this.drawGroundItems();
    this.drawTelegraphs();
    this.drawProjectiles();
    this.drawFx();
    this.drawBossOverlay();
    this.followCamera(dt);
  }

  private collectBodies(): Body[] {
    const bodies: Body[] = [];
    const state = this.ctx.room.state;
    state.enemies.forEach((e) => {
      // feature 5.2 — the boss is not a catalog enemy; use its def radius.
      const radius = e.boss
        ? (DATA.bosses[e.bossId]?.radius ?? 34)
        : (DATA.enemies[e.etype as EnemyType] ?? DATA.enemies.goblin).radius;
      bodies.push({ x: e.x, y: e.y, radius });
    });
    state.players.forEach((p, id) => {
      if (id === this.ctx.sessionId || p.dead || !p.cls) return;
      const r = DATA.classes[p.cls as ClassId]?.radius ?? DATA.players.hitboxRadius;
      bodies.push({ x: p.x, y: p.y, radius: r });
    });
    return bodies;
  }

  private followCamera(dt: number): void {
    const cam = this.cameras.main;
    const lx = this.ctx.local.renderX;
    const ly = this.ctx.local.renderY;
    const k = 1 - Math.pow(0.0015, dt);
    cam.scrollX += (lx - cam.width / 2 - cam.scrollX) * k;
    cam.scrollY += (ly - cam.height / 2 - cam.scrollY) * k;
  }

  private rigFor(
    map: Map<string, Rig> | Map<number, Rig>,
    id: string | number,
    sheet: string,
    tint: number,
  ): Rig {
    const m = map as Map<string | number, Rig>;
    let rig = m.get(id);
    if (!rig || rig.sheet !== sheet) {
      rig?.sprite.destroy();
      rig?.label.destroy();
      rig?.bar.destroy();
      const sprite = this.add.sprite(0, 0, sheet).setDepth(1).setScale(SPRITE_SCALE);
      sprite.setTint(tint);
      const label = this.add
        .text(0, 0, '', { fontFamily: 'monospace', fontSize: '11px', color: '#e8e8ef' })
        .setOrigin(0.5, 1)
        .setDepth(5000);
      const bar = this.add.graphics().setDepth(5000);
      rig = { sprite, label, bar, sheet };
      m.set(id, rig);
    }
    return rig;
  }

  private drawRig(
    rig: Rig,
    x: number,
    y: number,
    facing: number,
    moving: boolean,
    hp: number,
    maxHp: number,
    name: string,
    dead: boolean,
  ): void {
    rig.sprite.setPosition(x, y).setDepth(y);
    rig.sprite.setFlipX(Math.cos(facing) < -0.1);
    const anim = `${rig.sheet}-${moving && !dead ? 'walk' : 'idle'}`;
    if (rig.sprite.anims.currentAnim?.key !== anim) rig.sprite.play(anim, true);
    rig.sprite.setAlpha(dead ? 0.35 : 1);

    const top = y - 22;
    rig.label.setPosition(x, top - 8).setText(name);

    rig.bar.clear();
    if (!dead && maxHp > 0) {
      const w = 28;
      const frac = Phaser.Math.Clamp(hp / maxHp, 0, 1);
      rig.bar.fillStyle(0x2a0d0d).fillRect(x - w / 2, top - 4, w, 4);
      rig.bar.fillStyle(frac > 0.3 ? 0x3fbf5f : 0xd23a3a).fillRect(x - w / 2, top - 4, w * frac, 4);
    }
  }

  private syncPlayers(): void {
    const state = this.ctx.room.state;
    const seen = new Set<string>();

    state.players.forEach((p, id) => {
      if (!p.cls) return;
      seen.add(id);
      const rig = this.rigFor(
        this.players,
        id,
        `char-${p.cls as ClassId}`,
        CLASS_TINT[p.cls as ClassId] ?? 0xffffff,
      );
      let x = p.x;
      let y = p.y;
      let facing = p.facing;
      let moving = p.moving;
      if (id === this.ctx.sessionId) {
        x = this.ctx.local.renderX;
        y = this.ctx.local.renderY;
        facing = this.ctx.local.facing;
        moving = this.ctx.local.moving && !p.dead;
      } else {
        const pose = this.ctx.snaps.playerPose(id, NET_CONFIG.INTERP_DELAY_MS);
        if (pose) {
          x = pose.x;
          y = pose.y;
          facing = pose.facing;
        }
      }
      this.drawRig(rig, x, y, facing, moving, p.hp, p.maxHp, p.name, p.dead);
      // feature 5.1b — an enemy-applied debuff tints the hero sprite, same
      // priority (stun > slow > dot) + marker dot as the enemy status block.
      const classTint = CLASS_TINT[p.cls as ClassId] ?? 0xffffff;
      const dtint =
        !p.dead && p.stunned
          ? 0xffe066
          : !p.dead && p.slowed
            ? 0x66ccff
            : !p.dead && p.dotted
              ? 0xb69bff
              : classTint;
      rig.sprite.setTint(dtint);
      if (!p.dead && (p.stunned || p.dotted)) {
        rig.bar.fillStyle(p.stunned ? 0xffe066 : 0xb69bff, 0.9).fillCircle(x, y - 30, 3);
      }
    });

    for (const [id, rig] of this.players) {
      if (!seen.has(id)) {
        rig.sprite.destroy();
        rig.label.destroy();
        rig.bar.destroy();
        this.players.delete(id);
      }
    }
  }

  private syncEnemies(): void {
    const state = this.ctx.room.state;
    const seen = new Set<number>();

    state.enemies.forEach((e) => {
      seen.add(e.id);
      const etype = (e.etype || 'goblin') as EnemyType;
      // feature 5.2 — a boss uses its own sheet / deep-red tint / large scale and
      // shows its display name instead of the catalog name.
      const isBoss = e.boss === true;
      const bossId = e.bossId || 'warlord';
      const baseTint = isBoss
        ? (BOSS_TINT[bossId] ?? 0xc0392b)
        : (ENEMY_TINT[etype] ?? GOBLIN_TINT);
      const stats = DATA.enemies[etype] ?? DATA.enemies.goblin;
      const label = isBoss ? e.bossName || 'Boss' : stats.name;
      const rig = this.rigFor(
        this.enemies,
        e.id,
        isBoss ? bossSheet(bossId) : enemySheet(etype),
        baseTint,
      );
      let x = e.x;
      let y = e.y;
      let facing = e.facing;
      const pose = this.ctx.snaps.enemyPose(e.id, NET_CONFIG.INTERP_DELAY_MS);
      if (pose) {
        x = pose.x;
        y = pose.y;
        facing = pose.facing;
      }
      this.drawRig(rig, x, y, facing, e.moving, e.hp, e.maxHp, label, false);
      rig.sprite.setScale(isBoss ? SPRITE_SCALE * 2.4 : SPRITE_SCALE);
      // Feature 1.2 / 1.3b: tint debuffed enemies so they read at a glance
      // (stun > slow > dot for tint priority); feature 5.2 — a phase-transition
      // invuln boss shimmers pale; otherwise the per-etype / boss tint.
      let tint = e.stunned ? 0xffe066 : e.slowed ? 0x66ccff : e.dotted ? 0xb69bff : baseTint;
      if (isBoss && e.invuln) {
        const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 70);
        tint = Phaser.Display.Color.GetColor(200 + pulse * 55, 200 + pulse * 55, 220);
        rig.bar.lineStyle(3, 0xdfe4ff, 0.8).strokeCircle(x, y - 4, 30);
      }
      // feature 5.3b — a downed linked twin reads as a slumped grey shape with a
      // pale "reviving" ring.
      if (isBoss && e.downed) {
        tint = 0x6a6f7c;
        rig.bar.lineStyle(3, 0x7ad2ff, 0.7).strokeCircle(x, y - 4, 34);
      }
      rig.sprite.setTint(tint);
      if (e.stunned) {
        rig.bar.fillStyle(0xffe066, 0.9).fillCircle(x, y - 30, 3);
      } else if (e.dotted) {
        rig.bar.fillStyle(0xb69bff, 0.9).fillCircle(x, y - 30, 3);
      }
    });

    for (const [id, rig] of this.enemies) {
      if (!seen.has(id)) {
        rig.sprite.destroy();
        rig.label.destroy();
        rig.bar.destroy();
        this.enemies.delete(id);
      }
    }
  }

  /**
   * feature 3.4 — a small rarity-coloured marker per enemy item drop on the
   * floor. Rendered straight from the synced `groundItems` state (no
   * interpolation — the marks are static); a mark disappears when the server
   * removes the drop (picked up or the run ended). A gentle bob keeps it
   * readable against the floor.
   */
  private drawGroundItems(): void {
    const g = this.groundItemLayer;
    g.clear();
    const state = this.ctx.room.state;
    if (!state.groundItems) return;
    const bob = 2 * Math.sin(performance.now() / 200);
    state.groundItems.forEach((gi) => {
      const rarity = itemDef(gi.itemId)?.rarity ?? 'common';
      const color = rarity === 'epic' ? 0xa860e0 : rarity === 'rare' ? 0x4a90d9 : 0xb8c0cc;
      const y = gi.y + bob;
      // soft ground shadow
      g.fillStyle(0x000000, 0.25).fillEllipse(gi.x, gi.y + 6, 16, 7);
      // a small diamond gem
      g.fillStyle(color, 0.95);
      g.beginPath();
      g.moveTo(gi.x, y - 7);
      g.lineTo(gi.x + 6, y);
      g.lineTo(gi.x, y + 7);
      g.lineTo(gi.x - 6, y);
      g.closePath();
      g.fillPath();
      g.lineStyle(1.5, 0xffffff, 0.7).strokeCircle(gi.x, y, 9);
    });
  }

  /**
   * feature 5.3a — a translucent floor disc per active hazard zone, coloured by
   * kind (`damage` sickly green, `slow` pale blue), drawn beneath the entities.
   * Straight from the synced `hazards` state (static zones — no interpolation),
   * a mark clears when the server removes the hazard (expired or wave ended).
   */
  private drawHazards(): void {
    const g = this.hazardLayer;
    g.clear();
    const state = this.ctx.room.state;
    if (!state.hazards) return;
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 320);
    state.hazards.forEach((h) => {
      if (h.kind === 'burnOutside') {
        // feature 5.3c — the danger is OUTSIDE the ring: a bright molten stroke,
        // no fill (the safe ground is within).
        g.lineStyle(6, 0xff6a1a, 0.5 + 0.3 * pulse).strokeCircle(h.x, h.y, h.radius);
        g.lineStyle(2, 0xffd08a, 0.6).strokeCircle(h.x, h.y, h.radius - 4);
        return;
      }
      if (h.kind === 'safe') {
        // feature 5.3c — a cool blue safe disc (a cooling vent).
        g.fillStyle(0x3aa0e0, 0.16 + 0.06 * pulse).fillCircle(h.x, h.y, h.radius);
        g.lineStyle(2, 0x9fe0ff, 0.55).strokeCircle(h.x, h.y, h.radius);
        return;
      }
      const dmg = h.kind === 'damage';
      const fill = dmg ? 0x6faa38 : 0x63a8dc;
      const edge = dmg ? 0x9bd453 : 0xa9d8f0;
      g.fillStyle(fill, 0.14 + 0.05 * pulse).fillCircle(h.x, h.y, h.radius);
      g.lineStyle(2, edge, 0.45).strokeCircle(h.x, h.y, h.radius);
    });
  }

  /**
   * feature 1.5 — ground danger-zone for each active windup. A translucent red
   * disc + solid outline, with an inner disc that fills toward the edge as the
   * windup elapses (plus a pulse) so the impact reads at a glance.
   */
  private drawTelegraphs(): void {
    const g = this.telegraphLayer;
    g.clear();
    const state = this.ctx.room.state;
    if (!state.telegraphs) return;
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 90);
    state.telegraphs.forEach((t) => {
      const r = t.radius;
      const frac = Phaser.Math.Clamp(1 - t.remainingMs / (t.totalMs || 1), 0, 1);
      // feature 5.1b — hostile (enemy) danger-zones read amber-orange; the
      // player's own windups stay crimson so the two are distinct.
      const fill = t.hostile ? 0xffa000 : 0xff3a2a;
      const edge = t.hostile ? 0xffc24a : 0xff5a3a;
      g.fillStyle(fill, 0.14).fillCircle(t.x, t.y, r);
      g.fillStyle(edge, 0.28).fillCircle(t.x, t.y, r * frac);
      g.lineStyle(3, edge, 0.6 + 0.4 * pulse).strokeCircle(t.x, t.y, r);
      g.lineStyle(2, 0xffe0b0, 0.5).strokeCircle(t.x, t.y, r * frac);
    });
  }

  /** feature 1.5 — a moving mark per skillshot projectile, oriented along facing. */
  private drawProjectiles(): void {
    const g = this.projectileLayer;
    g.clear();
    const state = this.ctx.room.state;
    if (!state.projectiles) return;
    state.projectiles.forEach((pr) => {
      const cx = Math.cos(pr.facing);
      const sy = Math.sin(pr.facing);
      // feature 5.1b — hostile (enemy) shots are red/orange; player skillshots
      // stay lime-green.
      const streak = pr.hostile ? 0xff5a3a : 0xd6ff7a;
      const tip = pr.hostile ? 0xffc0a0 : 0xeaffb0;
      g.lineStyle(4, streak, 0.95).lineBetween(
        pr.x - cx * 12,
        pr.y - sy * 12,
        pr.x + cx * 14,
        pr.y + sy * 14,
      );
      g.fillStyle(tip, 0.95).fillCircle(pr.x + cx * 14, pr.y + sy * 14, 4);
    });
  }

  /**
   * feature 5.2 / 5.3b — the boss UI: bespoke pillar obstacles in world space,
   * and one screen-fixed health bar per boss entity (up to 2, stacked) across
   * the top — each with name + hp% + one pip per phase + a cast bar that fills
   * over `castTotalMs`. A downed linked twin (feature 5.3b) shows a "reviving in
   * N.Ns" state instead. All drawn straight from the synced state (no DOM),
   * cleared when no boss is present.
   */
  private drawBossOverlay(): void {
    const g = this.bossBarLayer;
    g.clear();
    this.bossPillarLayer.clear();
    for (const t of this.bossBarTexts) t.setText('');

    const bosses: EnemyView[] = [];
    this.ctx.room.state.enemies.forEach((e) => {
      if (e.boss) bosses.push(e);
    });
    if (bosses.length === 0) return;

    const def = DATA.bosses[bosses[0]!.bossId];
    for (const o of def?.pillars ?? []) {
      this.bossPillarLayer.fillStyle(0x2f2622, 1).fillRect(o.x, o.y, o.w, o.h);
      this.bossPillarLayer.lineStyle(2, 0x000000, 0.35).strokeRect(o.x, o.y, o.w, o.h);
    }

    const cam = this.cameras.main;
    const barW = Math.min(560, cam.width - 80);
    const x = (cam.width - barW) / 2;
    const rowH = 40;

    bosses.slice(0, 2).forEach((b, idx) => {
      const y = 18 + idx * rowH;
      const bdef = DATA.bosses[b.bossId];
      const frac = b.maxHp > 0 ? Phaser.Math.Clamp(b.hp / b.maxHp, 0, 1) : 0;
      g.fillStyle(0x000000, 0.55).fillRect(x - 8, y - 8, barW + 16, rowH);
      g.fillStyle(0x2a0d0d, 1).fillRect(x, y, barW, 14);
      const fillColor = b.downed ? 0x6a6f7c : b.invuln ? 0x9aa0b0 : 0xd23a3a;
      g.fillStyle(fillColor, 1).fillRect(x, y, barW * frac, 14);
      g.lineStyle(1, 0xffffff, 0.25).strokeRect(x, y, barW, 14);

      const phases = bdef?.phases ?? [];
      for (let i = 0; i < phases.length; i++) {
        g.fillStyle(i <= b.phaseIndex ? 0xffd27a : 0x555a66, 1).fillCircle(
          x + barW - 8 - i * 14,
          y + 7,
          4,
        );
      }

      if (b.downed) {
        const secs = (b.reviveTimerMs / 1000).toFixed(1);
        const cy = y + 20;
        const window = bdef?.reviveWindowMs ?? 4000;
        const rfrac = Phaser.Math.Clamp(1 - b.reviveTimerMs / window, 0, 1);
        g.fillStyle(0x1a1a2a, 1).fillRect(x, cy, barW, 9);
        g.fillStyle(0x7ad2ff, 1).fillRect(x, cy, barW * rfrac, 9);
        this.bossBarTexts[idx]!.setPosition(x, y - 8).setText(
          `${b.bossName}  DOWN  reviving in ${secs}s`,
        );
        return;
      }

      if (b.castAbility && b.castTotalMs > 0) {
        const cy = y + 20;
        const cfrac = Phaser.Math.Clamp(1 - b.castRemainingMs / b.castTotalMs, 0, 1);
        g.fillStyle(0x1a1a2a, 1).fillRect(x, cy, barW, 9);
        g.fillStyle(0xffb04a, 1).fillRect(x, cy, barW * cfrac, 9);
      }

      this.bossBarTexts[idx]!.setPosition(x, y - 8).setText(
        `${b.bossName}  ${Math.ceil(frac * 100)}%` +
          (b.castAbility ? `   casting: ${b.castAbility}` : ''),
      );
    });
  }

  private drawFx(): void {
    const now = performance.now();
    this.fxQueue = this.fxQueue.filter((f) => f.until > now);
    this.fxLayer.clear();
    for (const { fx } of this.fxQueue) {
      if (fx.kind === 'ranged' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(2, 0xffe27a, 0.9).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
      } else if (fx.kind === 'melee' && fx.tx !== undefined && fx.ty !== undefined) {
        const a = Math.atan2(fx.ty - fx.y, fx.tx - fx.x);
        this.fxLayer.lineStyle(3, 0xffffff, 0.85);
        this.fxLayer.beginPath();
        this.fxLayer.arc(fx.x, fx.y, 26, a - 0.7, a + 0.7);
        this.fxLayer.strokePath();
      } else if (fx.kind === 'enemyMelee' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(2, 0xff7a3a, 0.7).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
      } else if (fx.kind === 'hit') {
        this.fxLayer.fillStyle(0xffffff, 0.8).fillCircle(fx.x, fx.y, 6);
      } else if (fx.kind === 'enemyExplode') {
        // feature 5.1a — kamikaze (impBomber) detonation: a filled blast + ring.
        const r = fx.radius ?? 90;
        this.fxLayer.fillStyle(0xff6a2a, 0.3).fillCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(5, 0xff8a3a, 0.9).strokeCircle(fx.x, fx.y, r);
        this.fxLayer.fillStyle(0xffe0a0, 0.9).fillCircle(fx.x, fx.y, 14);
      } else if (fx.kind === 'enemyShoot' && fx.tx !== undefined && fx.ty !== undefined) {
        // feature 5.1b — hostile projectile muzzle flash along the shot line.
        const a = Math.atan2(fx.ty - fx.y, fx.tx - fx.x);
        this.fxLayer
          .lineStyle(3, 0xff5a3a, 0.85)
          .lineBetween(fx.x, fx.y, fx.x + Math.cos(a) * 26, fx.y + Math.sin(a) * 26);
        this.fxLayer.fillStyle(0xffc0a0, 0.9).fillCircle(fx.x, fx.y, 4);
      } else if (fx.kind === 'enemyHeal') {
        // feature 5.1b — goblinShaman heal pulse.
        this.fxLayer.lineStyle(3, 0x6ad0c0, 0.85).strokeCircle(fx.x, fx.y, 24);
        this.fxLayer.lineStyle(2, 0xbdfff2, 0.5).strokeCircle(fx.x, fx.y, 14);
      } else if (fx.kind === 'abilityBolt' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(3, 0x8ad4ff, 0.9).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.fillStyle(0x8ad4ff, 0.9).fillCircle(fx.tx, fx.ty, 7);
      } else if (fx.kind === 'abilityBlink' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(2, 0xc9a0ff, 0.7).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.lineStyle(2, 0xc9a0ff, 0.9).strokeCircle(fx.x, fx.y, 14);
        this.fxLayer.lineStyle(2, 0xc9a0ff, 0.9).strokeCircle(fx.tx, fx.ty, 14);
      } else if (fx.kind === 'abilityHeal') {
        this.fxLayer.lineStyle(3, 0x6bff9e, 0.9).strokeCircle(fx.x, fx.y, 22);
      } else if (fx.kind === 'abilityNova') {
        this.fxLayer.lineStyle(4, 0xffd27a, 0.85).strokeCircle(fx.x, fx.y, fx.radius ?? 80);
      } else if (fx.kind === 'abilityCharge' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(6, 0xffb04a, 0.5).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.lineStyle(2, 0xffe0a0, 0.9).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.fillStyle(0xffb04a, 0.5).fillCircle(fx.tx, fx.ty, 16);
      } else if (fx.kind === 'abilityCleave' && fx.tx !== undefined && fx.ty !== undefined) {
        const a = Math.atan2(fx.ty - fx.y, fx.tx - fx.x);
        this.fxLayer.lineStyle(5, 0xffffff, 0.85);
        this.fxLayer.beginPath();
        this.fxLayer.arc(fx.x, fx.y, fx.radius ?? 120, a - 1.05, a + 1.05);
        this.fxLayer.strokePath();
      } else if (fx.kind === 'abilityFireball' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(4, 0xff7a3a, 0.8).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.fillStyle(0xffb04a, 0.9).fillCircle(fx.tx, fx.ty, 14);
        this.fxLayer.fillStyle(0xffe0a0, 0.9).fillCircle(fx.tx, fx.ty, 7);
      } else if (fx.kind === 'abilityFrostNova') {
        this.fxLayer.lineStyle(4, 0x9fe8ff, 0.9).strokeCircle(fx.x, fx.y, fx.radius ?? 120);
        this.fxLayer
          .lineStyle(2, 0xd8f6ff, 0.6)
          .strokeCircle(fx.x, fx.y, (fx.radius ?? 120) * 0.65);
      } else if (fx.kind === 'abilityPierce' && fx.tx !== undefined && fx.ty !== undefined) {
        // muzzle flash — a short streak along the shot direction
        this.fxLayer.lineStyle(4, 0xd6ff7a, 0.95).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.fillStyle(0xeaffb0, 0.95).fillCircle(fx.x, fx.y, 5);
      } else if (fx.kind === 'abilityBarrier') {
        this.fxLayer.lineStyle(3, 0x8ad4ff, 0.9).strokeCircle(fx.x, fx.y, 24);
        this.fxLayer.lineStyle(2, 0xc9e8ff, 0.4).strokeCircle(fx.x, fx.y, 30);
      } else if (fx.kind === 'abilityBulwark') {
        this.fxLayer.lineStyle(4, 0x9ad0ff, 0.9).strokeCircle(fx.x, fx.y, 26);
        this.fxLayer.lineStyle(2, 0x9ad0ff, 0.5).strokeCircle(fx.x, fx.y, 34);
      } else if (fx.kind === 'abilityWhirlwind') {
        this.fxLayer.lineStyle(3, 0xffd27a, 0.8).strokeCircle(fx.x, fx.y, fx.radius ?? 120);
        this.fxLayer.lineStyle(2, 0xffffff, 0.5).strokeCircle(fx.x, fx.y, (fx.radius ?? 120) * 0.6);
      } else if (fx.kind === 'abilitySmite' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(4, 0xfff2a8, 0.9).lineBetween(fx.tx, fx.ty - 40, fx.tx, fx.ty);
        this.fxLayer.fillStyle(0xfff2a8, 0.9).fillCircle(fx.tx, fx.ty, 9);
      } else if (fx.kind === 'abilityCircleHeal') {
        this.fxLayer.lineStyle(4, 0x7bffb0, 0.85).strokeCircle(fx.x, fx.y, fx.radius ?? 160);
        this.fxLayer.lineStyle(2, 0xd8ffe8, 0.5).strokeCircle(fx.x, fx.y, (fx.radius ?? 160) * 0.6);
      } else if (fx.kind === 'abilityBlessing') {
        const r = fx.radius ?? 22;
        this.fxLayer.lineStyle(3, 0xffe07a, 0.9).strokeCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(2, 0xfff4c8, 0.5).strokeCircle(fx.x, fx.y, r * 0.65);
      } else if (fx.kind === 'abilityMoonfire' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(3, 0xc7b3ff, 0.9).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.fillStyle(0xc7b3ff, 0.9).fillCircle(fx.tx, fx.ty, 10);
        this.fxLayer.fillStyle(0xece3ff, 0.9).fillCircle(fx.tx, fx.ty, 5);
      } else if (fx.kind === 'abilityRegrowth') {
        this.fxLayer.fillStyle(0x7bff9e, 0.85);
        for (let i = 0; i < 4; i++) {
          const a = (i / 4) * Math.PI * 2;
          this.fxLayer.fillCircle(fx.x + Math.cos(a) * 16, fx.y + Math.sin(a) * 16 - 6, 3);
        }
      } else if (fx.kind === 'abilityRoots') {
        const r = fx.radius ?? 120;
        this.fxLayer.lineStyle(4, 0x9c7b4a, 0.9).strokeCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(2, 0x6bff9e, 0.6).strokeCircle(fx.x, fx.y, r * 0.7);
      } else if (fx.kind === 'abilityShadowBolt' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(4, 0x6a3aa0, 0.9).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.fillStyle(0x3a1060, 0.9).fillCircle(fx.tx, fx.ty, 11);
        this.fxLayer.fillStyle(0xc79bff, 0.8).fillCircle(fx.tx, fx.ty, 5);
      } else if (fx.kind === 'abilityCorruption' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(2, 0x7bd23a, 0.7).lineBetween(fx.x, fx.y, fx.tx, fx.ty);
        this.fxLayer.lineStyle(3, 0x7bd23a, 0.9).strokeCircle(fx.tx, fx.ty, 16);
        this.fxLayer.lineStyle(2, 0x4a8a2a, 0.6).strokeCircle(fx.tx, fx.ty, 22);
      } else if (fx.kind === 'abilityDrainLife' && fx.tx !== undefined && fx.ty !== undefined) {
        this.fxLayer.lineStyle(3, 0xb03a6a, 0.85).lineBetween(fx.tx, fx.ty, fx.x, fx.y);
        this.fxLayer.fillStyle(0xff8ab0, 0.8).fillCircle(fx.x, fx.y, 6);
        this.fxLayer.fillStyle(0x8a2a4a, 0.7).fillCircle(fx.tx, fx.ty, 8);
      } else if (fx.kind === 'abilityMeteor') {
        const r = fx.radius ?? 200;
        this.fxLayer.fillStyle(0xff6a2a, 0.28).fillCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(5, 0xff8a3a, 0.9).strokeCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(3, 0xffd27a, 0.8).strokeCircle(fx.x, fx.y, r * 0.55);
        this.fxLayer.fillStyle(0xffe0a0, 0.9).fillCircle(fx.x, fx.y, 16);
      } else if (fx.kind === 'abilityRapidFire') {
        this.fxLayer.lineStyle(3, 0xffe27a, 0.9).strokeCircle(fx.x, fx.y, 22);
        this.fxLayer.lineStyle(2, 0xffb04a, 0.6).strokeCircle(fx.x, fx.y, 30);
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          this.fxLayer.fillStyle(0xffe27a, 0.85);
          this.fxLayer.fillCircle(fx.x + Math.cos(a) * 30, fx.y + Math.sin(a) * 30, 3);
        }
      } else if (fx.kind === 'abilitySanctuary') {
        const r = fx.radius ?? 260;
        this.fxLayer.fillStyle(0x8affc0, 0.18).fillCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(5, 0x7bffb0, 0.9).strokeCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(2, 0xffffff, 0.5).strokeCircle(fx.x, fx.y, r * 0.62);
      } else if (fx.kind === 'abilityEntanglingStorm') {
        const r = fx.radius ?? 220;
        this.fxLayer.fillStyle(0x4a8a2a, 0.22).fillCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(5, 0x7bd23a, 0.9).strokeCircle(fx.x, fx.y, r);
        for (let i = 0; i < 3; i++) {
          this.fxLayer.lineStyle(2, 0x9c7b4a, 0.7).strokeCircle(fx.x, fx.y, r * (0.35 + i * 0.2));
        }
      } else if (fx.kind === 'abilitySoulHarvest') {
        const r = fx.radius ?? 240;
        this.fxLayer.fillStyle(0x6a3aa0, 0.22).fillCircle(fx.x, fx.y, r);
        this.fxLayer.lineStyle(5, 0xc79bff, 0.9).strokeCircle(fx.x, fx.y, r);
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2;
          this.fxLayer
            .lineStyle(2, 0x8a2a4a, 0.7)
            .lineBetween(fx.x + Math.cos(a) * r, fx.y + Math.sin(a) * r, fx.x, fx.y);
        }
      }
    }
  }
}
